import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Sistema centralizado de preservación de estado de la aplicación.
 *
 * #42 — Anti-bloat hardening (iOS):
 *  - `scrollPositions` queda acotado a las últimas MAX_SCROLL_ROUTES rutas
 *    (LRU). Antes crecía sin tope y la cadena en localStorage podía pasar
 *    de 100 KB en pocas sesiones de iOS.
 *  - Las escrituras de fondo (scroll/touchend) están fuertemente throttled
 *    y se descartan si nada cambió frente al último estado persistido.
 *  - El backup en `sessionStorage` solo se escribe en eventos de
 *    background (visibilitychange/blur/pagehide), no en cada save.
 *  - Eliminada la doble escritura diferida en iOS (100 ms / 50 ms).
 */

const APP_STATE_KEY = 'app-complete-state';
const STATE_TIMESTAMP_KEY = 'app-state-timestamp';
const STATE_EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 días
const MAX_SCROLL_ROUTES = 10;            // tope LRU de scrollPositions
const SCROLL_DELTA_THRESHOLD = 50;       // px mínimos para reescribir scroll
const SCROLL_THROTTLE_MS = 3000;         // antes 1000
const TOUCHEND_THROTTLE_MS = 2000;       // antes 500
const MAX_SERIALIZED_LENGTH = 16 * 1024; // 16 KB — alerta y poda defensiva

export interface SessionDetails {
  category?: string;
  level?: number;
  currentQuestion?: number;
  mode?: 'quiz' | 'study' | 'exam' | 'review';
  questionVersion?: 'full' | 'short';
  isFlipped?: boolean;
  timeRemaining?: number;
  examStarted?: boolean;
}

export interface AppState {
  route: string;
  timestamp: number;
  isQuizActive: boolean;
  isStudyActive: boolean;
  isExamActive: boolean;
  scrollPosition: number;
  scrollPositions: Record<string, number>;
  sessionDetails?: SessionDetails;
}

const DEFAULT_STATE: AppState = {
  route: '/',
  timestamp: 0,
  isQuizActive: false,
  isStudyActive: false,
  isExamActive: false,
  scrollPosition: 0,
  scrollPositions: {},
  sessionDetails: undefined,
};

// In-memory copy of the last persisted state — avoids a JSON.parse on every
// save and lets us short-circuit no-op writes.
let lastPersisted: AppState | null = null;
let lastPersistedSerialized = '';

const isAndroidUA = () => /Android/.test(navigator.userAgent);
const isIOSUA = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const isMobileUA = () => isAndroidUA() || isIOSUA();

/**
 * Mantiene scrollPositions acotado a las últimas N rutas vistas.
 * Garantiza que la ruta actual esté siempre presente (LRU "tocado").
 */
function pruneScrollPositions(
  positions: Record<string, number>,
  currentRoute: string
): Record<string, number> {
  const keys = Object.keys(positions);
  if (keys.length <= MAX_SCROLL_ROUTES) return positions;
  // Conservar la ruta actual + las últimas N-1 entradas insertadas.
  const tail = keys.slice(-MAX_SCROLL_ROUTES);
  const pruned: Record<string, number> = {};
  for (const k of tail) pruned[k] = positions[k];
  if (currentRoute && currentRoute in positions) {
    pruned[currentRoute] = positions[currentRoute];
  }
  return pruned;
}

function stateMaterialsDiffer(prev: AppState | null, next: AppState): boolean {
  if (!prev) return true;
  if (prev.route !== next.route) return true;
  if (prev.isQuizActive !== next.isQuizActive) return true;
  if (prev.isStudyActive !== next.isStudyActive) return true;
  if (prev.isExamActive !== next.isExamActive) return true;
  if (Math.abs((prev.scrollPosition || 0) - (next.scrollPosition || 0)) >= SCROLL_DELTA_THRESHOLD) return true;
  // sessionDetails: comparación superficial — cualquier cambio cuenta.
  const a = JSON.stringify(prev.sessionDetails ?? null);
  const b = JSON.stringify(next.sessionDetails ?? null);
  if (a !== b) return true;
  return false;
}

/**
 * Guarda el estado completo (con coalescencia y backup opcional).
 * `options.writeBackup=true` adicionalmente escribe a sessionStorage —
 * reservado para eventos de background, no para cada scroll.
 */
export const saveAppState = (
  state: Partial<AppState>,
  options?: { writeBackup?: boolean; force?: boolean }
) => {
  try {
    const currentState = lastPersisted ?? getAppState();
    const merged: AppState = {
      ...currentState,
      ...state,
      timestamp: Date.now(),
    };
    merged.scrollPositions = pruneScrollPositions(
      merged.scrollPositions ?? {},
      merged.route
    );

    if (!options?.force && !stateMaterialsDiffer(lastPersisted, merged)) {
      // Nada relevante cambió → no reescribimos localStorage.
      return;
    }

    const stateStr = JSON.stringify(merged);
    const timestampStr = merged.timestamp.toString();

    // Salvaguarda dura: si por algún motivo el blob explota, podamos scroll.
    let payload = stateStr;
    if (payload.length > MAX_SERIALIZED_LENGTH) {
      const trimmed: AppState = { ...merged, scrollPositions: {} };
      payload = JSON.stringify(trimmed);
      console.warn(
        '[App State] Payload exceeded threshold, pruning scrollPositions',
        { originalBytes: stateStr.length, prunedBytes: payload.length }
      );
    }

    localStorage.setItem(APP_STATE_KEY, payload);
    localStorage.setItem(STATE_TIMESTAMP_KEY, timestampStr);

    if (options?.writeBackup && isMobileUA()) {
      sessionStorage.setItem(APP_STATE_KEY + '_backup', payload);
      sessionStorage.setItem(STATE_TIMESTAMP_KEY + '_backup', timestampStr);
    }

    lastPersisted = merged;
    lastPersistedSerialized = payload;
  } catch (error) {
    console.error('[App State] Error saving:', error);
  }
};

export const getAppState = (): AppState => {
  try {
    let saved = localStorage.getItem(APP_STATE_KEY);
    let timestamp = localStorage.getItem(STATE_TIMESTAMP_KEY);

    if (isMobileUA() && (!saved || !timestamp)) {
      saved = sessionStorage.getItem(APP_STATE_KEY + '_backup');
      timestamp = sessionStorage.getItem(STATE_TIMESTAMP_KEY + '_backup');
      if (saved && timestamp) {
        localStorage.setItem(APP_STATE_KEY, saved);
        localStorage.setItem(STATE_TIMESTAMP_KEY, timestamp);
      }
    }

    if (saved && timestamp) {
      const state = JSON.parse(saved) as AppState;
      const savedTime = parseInt(timestamp, 10);
      if (Date.now() - savedTime < STATE_EXPIRATION_MS) {
        lastPersisted = state;
        lastPersistedSerialized = saved;
        return state;
      }
      clearAppState();
    }
  } catch (error) {
    console.error('[App State] Error getting state:', error);
  }
  return { ...DEFAULT_STATE, scrollPositions: {} };
};

export const clearAppState = () => {
  try {
    localStorage.removeItem(APP_STATE_KEY);
    localStorage.removeItem(STATE_TIMESTAMP_KEY);
    sessionStorage.removeItem(APP_STATE_KEY + '_backup');
    sessionStorage.removeItem(STATE_TIMESTAMP_KEY + '_backup');
    lastPersisted = null;
    lastPersistedSerialized = '';
  } catch (error) {
    console.error('[App State] Error clearing:', error);
  }
};

const isActiveRoute = (pathname: string) => ({
  isQuizActive: pathname.startsWith('/quiz'),
  isStudyActive: pathname.startsWith('/study'),
  isExamActive: pathname.startsWith('/exam-simulation') || pathname === '/real-exam',
});

export const useAppStatePreservation = () => {
  const location = useLocation();
  const lastScrollSaveRef = useRef<number>(0);
  const lastTouchSaveRef = useRef<number>(0);
  const visibilityListenerAttachedRef = useRef(false);

  // Guardar al cambiar de ruta.
  useEffect(() => {
    if (location.pathname.startsWith('/auth/')) return;

    const flags = isActiveRoute(location.pathname);
    const scrollPosition = window.scrollY || window.pageYOffset;
    const current = lastPersisted ?? getAppState();
    const scrollPositions = pruneScrollPositions(
      { ...current.scrollPositions, [location.pathname]: scrollPosition },
      location.pathname
    );

    saveAppState({
      route: location.pathname,
      ...flags,
      scrollPosition,
      scrollPositions,
    }, { force: true });
  }, [location.pathname]);

  // Listeners de background.
  useEffect(() => {
    if (visibilityListenerAttachedRef.current) return;
    visibilityListenerAttachedRef.current = true;

    const isMobile = isMobileUA();

    const captureCurrent = (writeBackup: boolean) => {
      if (location.pathname.startsWith('/auth/')) return;
      const flags = isActiveRoute(location.pathname);
      const scrollPosition = window.scrollY || window.pageYOffset;
      const current = lastPersisted ?? getAppState();
      const scrollPositions = pruneScrollPositions(
        { ...current.scrollPositions, [location.pathname]: scrollPosition },
        location.pathname
      );
      saveAppState(
        {
          route: location.pathname,
          ...flags,
          scrollPosition,
          scrollPositions,
        },
        { writeBackup }
      );
    };

    const handleVisibilityChange = () => {
      if (document.hidden) captureCurrent(true);
    };
    const handleBeforeUnload = () => captureCurrent(true);
    const handleBlur = () => captureCurrent(true);

    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollSaveRef.current < SCROLL_THROTTLE_MS) return;
      lastScrollSaveRef.current = now;
      captureCurrent(false);
    };

    const handleTouchEnd = () => {
      const now = Date.now();
      if (now - lastTouchSaveRef.current < TOUCHEND_THROTTLE_MS) return;
      lastTouchSaveRef.current = now;
      captureCurrent(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('freeze', handleBeforeUnload);
    document.addEventListener('resume', handleVisibilityChange);

    if (isMobile) {
      document.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('freeze', handleBeforeUnload);
      document.removeEventListener('resume', handleVisibilityChange);
      if (isMobile) document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [location.pathname]);

  return null;
};
